import React, { useState, useEffect, useMemo } from 'react';
import { resolveFrontendApiBase } from './utils/apiBase';
import {
  Modal,
  Button,
  Select,
  Checkbox,
  Input, 
  List,
  Alert,
  Tooltip,
  message,
  Space,
  Divider,
  Typography,
  Badge,
  Row,
  Col,
  Card,
  Radio,
  Tag
} from 'antd';
import {
  ExclamationCircleOutlined,
  WarningOutlined,
  SoundOutlined,
  QuestionCircleOutlined,
  DownOutlined,
  CheckCircleOutlined,
  StopOutlined,
  SearchOutlined,
  SwapOutlined,
  TranslationOutlined,
  WomanOutlined,
  ManOutlined,
  RedoOutlined,
  PauseOutlined
} from '@ant-design/icons';
import { getTargetLanguageFromPair, splitLanguagePair } from './utils/languagePairs';
import axios from 'axios';
import { estimateUSD, formatUSD, shouldShowDollarEquivalents } from './utils/costUtils';
import { voiceNamesByLanguage, getVoiceDisplayName, VoiceMappings, LanguageVoiceMappings } from './voiceConfig';
import { ChapterWordCounts } from './types';
import { countWordsIncludingMarkup } from './utils/wordcount';
import LoadingCard from './components/LoadingCard';
import './AudiobookCreation.css';

const { Text } = Typography;

const API_BASE = resolveFrontendApiBase();

// Utility function to count visible words, ignoring markup
const countWords = (text: string) => countWordsIncludingMarkup(text ?? '');

export type VoiceOption = {
  value: string;
  label: string;
  gender: 'Female' | 'Male';
  sampleUrl: string;
  provider: 'openai' | 'elevenlabs';
};

const voiceOptions: VoiceOption[] = [
  // OpenAI TTS voices
  { value: 'alloy', label: 'Amber Reyes', gender: 'Female', sampleUrl: '/samples/openai-alloy.mp3', provider: 'openai' },
  //{ value: 'coral', label: 'Chloe Costa', gender: 'Female', sampleUrl: '/samples/openai-coral.mp3', provider: 'openai' },
  { value: 'nova', label: 'Nora Lin', gender: 'Female', sampleUrl: '/samples/openai-nova.mp3', provider: 'openai' },
  //{ value: 'sage', label: 'Sarah Weber', gender: 'Female', sampleUrl: '/samples/openai-sage.mp3', provider: 'openai' },
  { value: 'shimmer', label: 'Sophia Navarro', gender: 'Female', sampleUrl: '/samples/openai-shimmer.mp3', provider: 'openai' },
  //{ value: 'ash', label: 'Atlas Vega', gender: 'Male', sampleUrl: '/samples/openai-ash.mp3', provider: 'openai' },  
  //{ value: 'ballad', label: 'Blake Morgan', gender: 'Male', sampleUrl: '/samples/openai-ballad.mp3', provider: 'openai' },  
  { value: 'echo', label: 'Ethan Novak', gender: 'Male', sampleUrl: '/samples/openai-echo.mp3', provider: 'openai' },
  { value: 'fable', label: 'Flynn Romano', gender: 'Male', sampleUrl: '/samples/openai-fable.mp3', provider: 'openai' },
  { value: 'onyx', label: 'Oliver Santos', gender: 'Male', sampleUrl: '/samples/openai-onyx.mp3', provider: 'openai' },
  //{ value: 'verse', label: 'Victor Park', gender: 'Male', sampleUrl: '/samples/openai-verse.mp3', provider: 'openai' },
  
  // ElevenLabs voices (eleven_multilingual_v2 only)
  { value: '9BWtsMINqrJLrRacOk9x', label: 'Aria', gender: 'Female', sampleUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/9BWtsMINqrJLrRacOk9x/405766b8-1f4e-4d3c-aba1-6f25333823ec.mp3', provider: 'elevenlabs' },
  { value: 'EXAVITQu4vr4xnSDxMaL', label: 'Sarah', gender: 'Female', sampleUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/EXAVITQu4vr4xnSDxMaL/01a3e33c-6e99-4ee7-8543-ff2216a32186.mp3', provider: 'elevenlabs' },
  { value: 'FGY2WhTYpPnrIDTdsKH5', label: 'Laura', gender: 'Female', sampleUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/FGY2WhTYpPnrIDTdsKH5/67341759-ad08-41a5-be6e-de12fe448618.mp3', provider: 'elevenlabs' },
  { value: 'XB0fDUnXU5powFXDhCwa', label: 'Charlotte', gender: 'Female', sampleUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/XB0fDUnXU5powFXDhCwa/942356dc-f10d-4d89-bda5-4f8505ee038b.mp3', provider: 'elevenlabs' },
  { value: 'Xb7hH8MSUJpSbSDYk0k2', label: 'Alice', gender: 'Female', sampleUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/Xb7hH8MSUJpSbSDYk0k2/d10f7534-11f6-41fe-a012-2de1e482d336.mp3', provider: 'elevenlabs' },
  { value: 'XrExE9yKIg1WjnnlVkGX', label: 'Matilda', gender: 'Female', sampleUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/XrExE9yKIg1WjnnlVkGX/b930e18d-6b4d-466e-bab2-0ae97c6d8535.mp3', provider: 'elevenlabs' },
  { value: 'cgSgspJ2msm6clMCkdW9', label: 'Jessica', gender: 'Female', sampleUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/cgSgspJ2msm6clMCkdW9/56a97bf8-b69b-448f-846c-c3a11683d45a.mp3', provider: 'elevenlabs' },
  { value: 'pFZP5JQG7iQjIQuC4Bku', label: 'Lily', gender: 'Female', sampleUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/pFZP5JQG7iQjIQuC4Bku/89b68b35-b3dd-4348-a84a-a3c13a3c2b30.mp3', provider: 'elevenlabs' },
  { value: 'SAz9YHcvj6GT2YYXdXww', label: 'River', gender: 'Female', sampleUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/SAz9YHcvj6GT2YYXdXww/e6c95f0b-2227-491a-b3d7-2249240decb7.mp3', provider: 'elevenlabs' },
  { value: 'IKne3meq5aSn9XLyUdCD', label: 'Charlie', gender: 'Male', sampleUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/IKne3meq5aSn9XLyUdCD/102de6f2-22ed-43e0-a1f1-111fa75c5481.mp3', provider: 'elevenlabs' },
  { value: 'JBFqnCBsd6RMkjVDRZzb', label: 'George', gender: 'Male', sampleUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/JBFqnCBsd6RMkjVDRZzb/e6206d1a-0721-4787-aafb-06a6e705cac5.mp3', provider: 'elevenlabs' },
  { value: 'N2lVS1w4EtoT3dr4eOWO', label: 'Callum', gender: 'Male', sampleUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/N2lVS1w4EtoT3dr4eOWO/ac833bd8-ffda-4938-9ebc-b0f99ca25481.mp3', provider: 'elevenlabs' },
  { value: 'TX3LPaxmHKxFdv7VOQHJ', label: 'Liam', gender: 'Male', sampleUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/TX3LPaxmHKxFdv7VOQHJ/63148076-6363-42db-aea8-31424308b92c.mp3', provider: 'elevenlabs' },
  { value: 'bIHbv24MWmeRgasZH58o', label: 'Will', gender: 'Male', sampleUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/bIHbv24MWmeRgasZH58o/8caf8f3d-ad29-4980-af41-53f20c72d7a4.mp3', provider: 'elevenlabs' },
  { value: 'cjVigY5qzO86Huf0OWal', label: 'Eric', gender: 'Male', sampleUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/cjVigY5qzO86Huf0OWal/d098fda0-6456-4030-b3d8-63aa048c9070.mp3', provider: 'elevenlabs' },
  { value: 'iP95p4xoKVk53GoZ742B', label: 'Chris', gender: 'Male', sampleUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/iP95p4xoKVk53GoZ742B/3f4bde72-cc48-40dd-829f-57fbf906f4d7.mp3', provider: 'elevenlabs' },
  { value: 'nPczCjzI2devNBz1zQrb', label: 'Brian', gender: 'Male', sampleUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/nPczCjzI2devNBz1zQrb/2dd3e72c-4fd3-42f1-93ea-abc5d4e5aa1d.mp3', provider: 'elevenlabs' },
  { value: 'onwK4e9ZLuTAKqWW03F9', label: 'Daniel', gender: 'Male', sampleUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/onwK4e9ZLuTAKqWW03F9/7eee0236-1a72-4b86-b303-5dcadc007ba9.mp3', provider: 'elevenlabs' },
  { value: 'pqHfZKP75CvOlQylNhV4', label: 'Bill', gender: 'Male', sampleUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/pqHfZKP75CvOlQylNhV4/d782b3ff-84ba-4029-848c-acf01285524d.mp3', provider: 'elevenlabs' },
];

const aiModelOptions = [
  { value: 'OpenAI', label: 'OpenAI' },
  { value: 'ElevenLabs', label: 'ElevenLabs' },
];

export const filterVoicesByGender = (voices: VoiceOption[], filter: 'all' | 'Female' | 'Male'): VoiceOption[] => {
  if (filter === 'all') {
    return voices;
  }
  return voices.filter((voice) => voice.gender === filter);
};

export interface ModalChapter {
  id: string;
  title: string;
  included: boolean;
  wordCount?: number; // Deprecated - will use chapterWordCounts instead
}

type InitialAudiobookChapterStatus = {
  status?: string;
  voice?: string | null;
};

type InitialAudiobookSectionStatus = {
  status?: string;
  hasAudio?: boolean;
  has_audio?: boolean;
  voice?: string;
  original_text?: string;
  processed_text?: string;
};

type InitialAudiobookStatus = {
  chapters?: Record<string, InitialAudiobookChapterStatus>;
  intro?: InitialAudiobookSectionStatus;
  outro?: InitialAudiobookSectionStatus;
};

const normalizeInitialAudiobookSectionStatus = (
  section: InitialAudiobookSectionStatus | undefined,
): {
  status: string;
  hasAudio: boolean;
  voice?: string;
  original_text?: string;
  processed_text?: string;
} => ({
  status: section?.status || 'no_audio',
  hasAudio: Boolean(section?.hasAudio ?? section?.has_audio),
  voice: section?.voice,
  original_text: section?.original_text,
  processed_text: section?.processed_text,
});

interface CreateOptions {
  version: string;
  voice: string;
  selectedChapters: string[];
  introText: string;
  outroText: string;
  includeChapterTitles: boolean;
  aiModel: string;
}

interface AudiobookModalProps {
  isVisible: boolean;
  onCancel: () => void;
  onAfterOpenChange?: (open: boolean) => void;
  onCreateAudiobook: (opts: CreateOptions) => Promise<void>;
  creditBalance: any;
  planId?: string;
  chapters: ModalChapter[];
  errorMessages: string[];
  warningMessages: string[];
  versionOptions?: Array<{
    value: string;
    label: string;
  }>;
  onVersionChange?: (version: string) => void;  
  book?: {  // Add book information
    title: string;
    author: string;
    bookId: string;
  };
  version?: string;
  onTranslateText?: (text: string, targetLang: string) => Promise<string>;  
  bookLanguage?: string;
  chapterWordCounts: ChapterWordCounts;
  initialChapterAudioStatus?: InitialAudiobookStatus | null;
  skipChapterStatusFetch?: boolean;
}

export type ChapterWordCountLookup = {
  chapterWordCounts: ChapterWordCounts;
  chapterId: string;
  selectedVersion: string;
  bookLanguage: string;
};

export const resolveChapterWordCount = ({
  chapterWordCounts,
  chapterId,
  selectedVersion,
  bookLanguage,
}: ChapterWordCountLookup): number => {
  const counts = chapterWordCounts[chapterId];
  if (!counts) return 0;

  const translations = counts.translations || {};
  const version = (selectedVersion || '').trim().toLowerCase();

  if (version.startsWith('original')) {
    if (version.includes('proofread')) {
      return counts.originalProofread ?? counts.original ?? 0;
    }
    return counts.original ?? 0;
  }

  if (version.includes('-')) {
    const target = (getTargetLanguageFromPair(selectedVersion) || '')
      .replace(/proofread/i, '')
      .replace(/[()]/g, '')
      .trim();

    const pair = `${bookLanguage}-${target}`;
    const translation = translations[pair];
    if (!translation) {
      return 0;
    }

    if (version.includes('proofread')) {
      return translation.proofread ?? translation.translated ?? 0;
    }
    return translation.translated ?? 0;
  }

  return 0;
};

export const shouldIncludeChapterByDefault = (wordCount: number, status?: string) =>
  wordCount > 0 && status !== 'all_audio' && status !== 'error';

export type VoiceSamplePlaybackOptions = {
  primaryUrl: string;
  fallbackUrl?: string | null;
  audioFactory: (url: string) => HTMLAudioElement;
  onStart: (audio: HTMLAudioElement) => void;
  onReset: () => void;
  notifyError: (message: string) => void;
};

export const playSampleWithFallback = async ({
  primaryUrl,
  fallbackUrl,
  audioFactory,
  onStart,
  onReset,
  notifyError,
}: VoiceSamplePlaybackOptions): Promise<void> => {
  const playUrl = async (url: string) => {
    const audio = audioFactory(url);
    audio.addEventListener('ended', onReset);
    onStart(audio);
    await audio.play();
  };

  try {
    await playUrl(primaryUrl);
    return;
  } catch {
    if (fallbackUrl && fallbackUrl !== primaryUrl) {
      try {
        await playUrl(fallbackUrl);
        return;
      } catch {
        notifyError('Could not play voice sample. Please try again later.');
        onReset();
        return;
      }
    }
    notifyError('Could not play voice sample. Please try again later.');
    onReset();
  }
};

const AudiobookCreationModal: React.FC<AudiobookModalProps> = ({
  isVisible,
  onCancel,
  onAfterOpenChange,
  onCreateAudiobook,
  creditBalance,
  planId,
  chapters,
  errorMessages,
  warningMessages,
  versionOptions,
  onVersionChange,
  book = { title: '', author: '', bookId: '' }, 
  onTranslateText,  
  version = 'Original',
  bookLanguage = 'English',
  chapterWordCounts,
  initialChapterAudioStatus,
  skipChapterStatusFetch = false,
}) => {
  const [selectedVersion, setSelectedVersion] = useState<string>('Original');
  const [selectedAiModel, setSelectedAiModel] = useState<string>('OpenAI'); 
  const [selectedVoice, setSelectedVoice] = useState<string>('');
  const [introText, setIntroText] = useState<string>('');
  const [outroText, setOutroText] = useState<string>('');
  const [includedChapters, setIncludedChapters] = useState<Set<string>>(new Set());
  const [filterText, setFilterText] = useState<string>('');
  const [rangeSelectionStart, setRangeSelectionStart] = useState<number | null>(null);
  const [isTranslatingIntro, setIsTranslatingIntro] = useState<boolean>(false);
  const [isTranslatingOutro, setIsTranslatingOutro] = useState<boolean>(false);
  const [isCostDetailsVisible, setIsCostDetailsVisible] = useState<boolean>(false);
    
  // Word count tracking
  const [introWordCount, setIntroWordCount] = useState<number>(0);
  const [outroWordCount, setOutroWordCount] = useState<number>(0);
  const [totalSelectedWordCount, setTotalSelectedWordCount] = useState<number>(0);
  const [genderFilter, setGenderFilter] = useState<'all' | 'Female' | 'Male'>('all');
  const [introEdited, setIntroEdited] = useState<boolean>(false);
  const [outroEdited, setOutroEdited] = useState<boolean>(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  
  // New state for audio status tracking
  const [chapterAudioStatus, setChapterAudioStatus] = useState<{[key: string]: string}>({});
  const [isLoadingStatus, setIsLoadingStatus] = useState<boolean>(false);
  const [statusError, setStatusError] = useState<string>('');
  const [chapterVoices, setChapterVoices] = useState<{[key: string]: string | null}>({});
  
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const [isUnsupportedLanguage, setIsUnsupportedLanguage] = useState<boolean>(false);
  const [unsupportedLanguageName, setUnsupportedLanguageName] = useState<string>('');

  const [introEnabled, setIntroEnabled] = useState<boolean>(true);
  const [outroEnabled, setOutroEnabled] = useState<boolean>(true);
  const [includeChapterTitles, setIncludeChapterTitles] = useState<boolean>(false);
  const [translatedWordCounts, setTranslatedWordCounts] = useState<Record<string,number>>({})
  


const [introAudioStatus, setIntroAudioStatus] = useState<{ 
  status: string; 
  hasAudio: boolean; 
  voice?: string;
  original_text?: string;
  processed_text?: string;
}>({ 
  status: 'no_audio', 
  hasAudio: false 
});

const [outroAudioStatus, setOutroAudioStatus] = useState<{ 
  status: string; 
  hasAudio: boolean; 
  voice?: string;
  original_text?: string;
  processed_text?: string;
}>({ 
  status: 'no_audio', 
  hasAudio: false 
});

  // Check if a translation is selected
  const isTranslationSelected = () => {
    return selectedVersion && 
           selectedVersion.toLowerCase() !== 'original' && 
           selectedVersion.toLowerCase() !== 'original-proofread' && 
           selectedVersion.includes('-');
  };


  // Extract target language from version string
  const getTargetLanguage = () => {
    if (!isTranslationSelected()) return '';

    return splitLanguagePair(selectedVersion).targetLang;
  };

  // Helper function to get the correct word count for a chapter based on the selected version
const getChapterWordCount = (chapterId: string): number =>
  resolveChapterWordCount({
    chapterWordCounts,
    chapterId,
    selectedVersion,
    bookLanguage,
  });


  const chaptersFingerprint = useMemo(() => {
    if (!chapters || chapters.length === 0) return '';
    return chapters.map(c => `${c.id}:${getChapterWordCount(c.id)}`).join(',');
  }, [chapters, chapterWordCounts, selectedVersion]);

// Get available voices based on selected AI model
  const getAvailableVoices = () => {
    // Convert selectedAiModel to lowercase for comparison with voice provider values
    const providerToMatch = selectedAiModel.toLowerCase();
  const providerVoices = voiceOptions.filter(voice => voice.provider === providerToMatch);
  return filterVoicesByGender(providerVoices, genderFilter);
  };

// Fetch chapter audio status
const fetchChapterAudioStatus = async (explicitVersion?: string) => {
  const versionToUse = explicitVersion !== undefined ? explicitVersion : selectedVersion;
  
  if (!book.bookId || !versionToUse) return;

  const language = getCurrentLanguage();
  const isSupported = !!voiceNamesByLanguage[language];
  
  if (!isSupported) {
    // Language is not supported, skip API call and reset states
    setIsLoadingStatus(false);
    setStatusError('');
    setChapterAudioStatus({});
    setChapterVoices({});
    setIntroAudioStatus({ status: 'no_audio', hasAudio: false });
    setOutroAudioStatus({ status: 'no_audio', hasAudio: false });
    return;
  }
  
  setIsLoadingStatus(true);
  setStatusError('');
  
  try {
    const response = await axios.get(
      `${API_BASE}/check-chapter-audio-status/${book.bookId}`,
      { params: { version: versionToUse } }
    );
	
    if (response.data && response.data.success) {
      // Extract the chapter status from the response
      const chapterStatuses = response.data.data.chapters || {};
      
      // Create a map of chapter ID to audio status
      const statusMap: {[key: string]: string} = {};
      const voiceMap: {[key: string]: string | null} = {};
	  
      Object.entries(chapterStatuses).forEach(([chapterId, data]: [string, any]) => {
        statusMap[chapterId] = data.status;
		voiceMap[chapterId] = data.voice;
      });
      
      setChapterAudioStatus(statusMap);
	  setChapterVoices(voiceMap);
      
      // Extract intro/outro status
      setIntroAudioStatus(response.data.data.intro || { status: 'no_audio', hasAudio: false });
      setOutroAudioStatus(response.data.data.outro || { status: 'no_audio', hasAudio: false });
    } else {
      console.error('Failed to fetch chapter audio status:', response.data?.error);
      setStatusError(response.data?.error || 'Failed to fetch chapter audio status');
    }
  } catch (error) {
    console.error('Error fetching chapter audio status:', error);
    setStatusError('Error checking chapter audio status. Please try again.');
  } finally {
    setIsLoadingStatus(false);
  }
};

  useEffect(() => {
    if (version) {
      setSelectedVersion(version);
      // Notify about version change if needed
      if (onVersionChange) {
        onVersionChange(version);
      }
    }
  }, [version, onVersionChange]);

useEffect(() => {
  if (isVisible) {
    // Reset to OpenAI on open
    setSelectedVoice('');
    setSelectedAiModel('OpenAI'); // Fixed: Changed from 'openai' to 'OpenAI'

    // Reset other UI bits
    setFilterText('');
    setRangeSelectionStart(null);
    setIntroEdited(false);
    setOutroEdited(false);
    
    // Clear audio status
    setChapterAudioStatus({});
    setStatusError('');
    
    // Reset load flag
    setInitialLoadComplete(false);

    // Stop any playing audio
    stopPlayingAudio();

    if (initialChapterAudioStatus) {
      const statusMap: {[key: string]: string} = {};
      const voiceMap: {[key: string]: string | null} = {};

      Object.entries(initialChapterAudioStatus.chapters || {}).forEach(([chapterId, data]) => {
        statusMap[chapterId] = data.status || 'no_audio';
        voiceMap[chapterId] = data.voice ?? null;
      });

      setChapterAudioStatus(statusMap);
      setChapterVoices(voiceMap);
      setIntroAudioStatus(normalizeInitialAudiobookSectionStatus(initialChapterAudioStatus.intro));
      setOutroAudioStatus(normalizeInitialAudiobookSectionStatus(initialChapterAudioStatus.outro));
      setIsLoadingStatus(false);
      setInitialLoadComplete(true);
      return;
    }

    if (skipChapterStatusFetch) {
      setIsLoadingStatus(false);
      setInitialLoadComplete(true);
      return;
    }
	
	fetchChapterAudioStatus();
    
  }
}, [isVisible, book.bookId, initialChapterAudioStatus, skipChapterStatusFetch]);

  // Fetch chapter audio status when version changes
useEffect(() => {
  if (isVisible && book.bookId && selectedVersion && initialLoadComplete && !skipChapterStatusFetch && !initialChapterAudioStatus) {
    // Only fetch if it's not the initial load
    fetchChapterAudioStatus();
  }
}, [isVisible, selectedVersion, book.bookId, initialLoadComplete, skipChapterStatusFetch, initialChapterAudioStatus]);

  // Update chapter selection based on audio status
  useEffect(() => {
    if (isVisible) {
      // Only select chapters that have word count > 0 and don't have complete audio
      // and don't have errors
      const validChapters = chapters
        .filter(c => {
          const status = chapterAudioStatus[c.id];
          const wordCount = getChapterWordCount(c.id);
          return shouldIncludeChapterByDefault(wordCount, status);
        })
        .map(c => c.id);
      
      setIncludedChapters(new Set(validChapters));
    }
  }, [isVisible, selectedVersion, chaptersFingerprint, chapterAudioStatus]);

  // Reset voice selection when AI model changes
  useEffect(() => {
    setSelectedVoice('');
  }, [selectedAiModel]);

  // Update intro word count
  useEffect(() => {
    setIntroWordCount(countWords(introText));
  }, [introText]);

  // Update outro word count
  useEffect(() => {
    setOutroWordCount(countWords(outroText));
  }, [outroText]);

  // Update total word count when selected chapters change
  useEffect(() => {
    const totalWords = chapters
      .filter(chap => includedChapters.has(chap.id))
      .reduce((sum, chap) => sum + getChapterWordCount(chap.id), 0);
    setTotalSelectedWordCount(totalWords);
  }, [chapters, includedChapters, chapterWordCounts, selectedVersion]);

  //check for unsupported languages
  useEffect(() => {
    if (isVisible) {
      const language = getCurrentLanguage();
      const isSupported = !!voiceNamesByLanguage[language];
      setIsUnsupportedLanguage(!isSupported);
      if (!isSupported) {
        setUnsupportedLanguageName(language);
      }
    }
  }, [selectedVersion, isVisible, bookLanguage]);

useEffect(() => {
  if (isUnsupportedLanguage) {
    setSelectedVoice('');
  }
}, [isUnsupportedLanguage]);

  const toggleChapterInclusion = (id: string, index: number, shiftKey: boolean = false) => {
    // Get the chapter
    const chapter = chapters.find(c => c.id === id);
    
    // Get chapter audio status
    const status = chapterAudioStatus[id];
    const wordCount = getChapterWordCount(id);
    
    // Skip if chapter has 0 words, complete audio, or error
    if (!chapter || 
        wordCount === 0 || 
        status === 'all_audio' || 
        status === 'error') {
      return;
    }

    // If shift key is pressed and we have a previous selection, handle range selection
    if (shiftKey && rangeSelectionStart !== null) {
      const start = Math.min(rangeSelectionStart, index);
      const end = Math.max(rangeSelectionStart, index);
      const chaptersInRange = filteredChapters
        .slice(start, end + 1)
        .filter(c => {
          const status = chapterAudioStatus[c.id];
          const wordCount = getChapterWordCount(c.id);
          return wordCount > 0 && 
                 status !== 'all_audio' && 
                 status !== 'error'; 
        })
        .map(c => c.id);
      
      // Determine if we're adding or removing based on the clicked chapter's state
      const isAdding = !includedChapters.has(id);
      
      setIncludedChapters(prev => {
        const next = new Set(prev);
        chaptersInRange.forEach(chapterId => {
          if (isAdding) {
            next.add(chapterId);
          } else {
            next.delete(chapterId);
          }
        });
        return next;
      });
      
      // Reset range selection start
      setRangeSelectionStart(null);
    } else {
      // Normal single chapter toggle
      setIncludedChapters(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      
      // Set this as potential start of a range
      setRangeSelectionStart(index);
    }
  };

  const handleChapterClick = (id: string, index: number, e: React.MouseEvent) => {
    toggleChapterInclusion(id, index, e.shiftKey);
  };

  const selectAll = () => {
    // Only select chapters with word count > 0, no complete audio, and no errors
    const validChapters = chapters
      .filter(chapter => {
        const status = chapterAudioStatus[chapter.id];
        const wordCount = getChapterWordCount(chapter.id);
        return wordCount > 0 && 
               status !== 'all_audio' && 
               status !== 'error';
      })
      .map(c => c.id);
    
    setIncludedChapters(new Set(validChapters));
  };

  const deselectAll = () => {
    setIncludedChapters(new Set());
  };

  const invertSelection = () => {
    setIncludedChapters(prev => {
      const next = new Set<string>();
      chapters.forEach(chap => {
        const status = chapterAudioStatus[chap.id];
        const wordCount = getChapterWordCount(chap.id);
        // Only include chapters with words, no complete audio, and no errors
        if (wordCount > 0 && 
            status !== 'all_audio' && 
            status !== 'error' && 
            !prev.has(chap.id)) {
          next.add(chap.id);
        }
      });
      return next;
    });
  };

  const playSample = (url: string, provider: string, voiceId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (playingVoiceId === voiceId && currentAudio) {
      stopPlayingAudio();
      return;
    }
    
    stopPlayingAudio();
    
    const primaryUrl = provider === 'openai' ? getLanguageSampleUrl(url.split('-')[1].split('.')[0]) : url;
    
    void playSampleWithFallback({
      primaryUrl,
      fallbackUrl: provider === 'openai' ? url : null,
      audioFactory: (src) => new Audio(src),
      onStart: (audio) => {
        setCurrentAudio(audio);
        setPlayingVoiceId(voiceId);
      },
      onReset: () => {
        setCurrentAudio(null);
        setPlayingVoiceId(null);
      },
      notifyError: (msg) => message.error(msg),
    });
  };

  // Filter chapters based on search text
  const filteredChapters = filterText 
    ? chapters.filter(c => c.title.toLowerCase().includes(filterText.toLowerCase()))
    : chapters;

  const generateDefaultIntroText = (): string => {
    if (!book.title || !book.author) return '';
    const voiceName = selectedVoice ? getVoiceNameForLanguage(selectedVoice) : 'AI';
    return `"${book.title}", written by ${book.author}. Narrated by ${voiceName}.`;
  };

  const generateDefaultOutroText = (): string => {
    if (!book.title || !book.author) return '';
    const voiceName = selectedVoice ? getVoiceNameForLanguage(selectedVoice) : 'AI';
    return `We hope you have enjoyed this presentation of "${book.title}", written by ${book.author}. Narrated by ${voiceName}.`;
  };
    
  const getVoiceNameFromLabel = (label: string): string => {
    // Extract the name portion before the parenthesis
    const match = label.match(/^(.*?)\s*\(/);
    return match ? match[1].trim() : label;
  };  
    
  useEffect(() => {
    if (isVisible) {
      if (!introText) setIntroText(generateDefaultIntroText());
      if (!outroText) setOutroText(generateDefaultOutroText());
    }
  }, [isVisible, book.title, book.author]);  

  useEffect(() => {
    if (selectedVoice && isVisible) {
      if (!introEdited) {
        setIntroText(generateDefaultIntroText());
      }
      if (!outroEdited) {
        setOutroText(generateDefaultOutroText());
      }
    }
  }, [selectedVoice, selectedVersion, introEdited, outroEdited]);

  
  // Function to translate text
  const translateText = async (text: string, type: 'intro' | 'outro') => {
    if (!text.trim() || !isTranslationSelected() || !onTranslateText) return;
    
    const targetLang = getTargetLanguage();
    if (!targetLang) return;
    
    if (type === 'intro') setIsTranslatingIntro(true);
    else setIsTranslatingOutro(true);
    
    try {
      const translatedText = await onTranslateText(text, targetLang);
      
      if (translatedText) {
        if (type === 'intro') setIntroText(translatedText);
        else setOutroText(translatedText);
        message.success(`${type === 'intro' ? 'Intro' : 'Outro'} credits translated to ${targetLang}`);
      } else {
        message.error('Translation failed');
      }
    } catch (error) {
      console.error(`Error translating ${type} text:`, error);
      message.error(`Failed to translate ${type} text`);
    } finally {
      if (type === 'intro') setIsTranslatingIntro(false);
      else setIsTranslatingOutro(false);
    }
  };

  const formatVersionLabel = (label: string) => {
    // Check if this is a translation label (contains " to ")
    if (label.includes(" to ")) {
      // Extract the part after "to " which preserves any suffix like "(Proofread)"
      const afterTo = label.substring(label.indexOf(" to ") + 4);
      return afterTo;
    }
    // Return original label for non-translation versions
    return label;
  };


  const getCurrentLanguage = (): string => {
    // For original version, default to English
	// console.log("selectedVersion: "+selectedVersion);
	// console.log("bookLanguage: "+bookLanguage);
    if (!selectedVersion || 
        selectedVersion.toLowerCase() === 'original' || 
        selectedVersion.toLowerCase() === 'original-proofread') {
      return bookLanguage;
    }
    
    // Get target language from version string
    const targetLang = getTargetLanguage();
    
    if (targetLang) {
      // Normalize for comparison
      const normalizedTargetLang = targetLang.toLowerCase().trim();
      
      // Match to our supported languages
      if (normalizedTargetLang.includes('german')) {
        return 'German';
      } else if (normalizedTargetLang.includes('french')) {
        return 'French';
      } else if (normalizedTargetLang.includes('italian')) {
        return 'Italian';
      } else if (normalizedTargetLang.includes('dutch')) {
        return 'Dutch';
      } else if (normalizedTargetLang.includes('portuguese')) {
        if (normalizedTargetLang.includes('european')) {
          return 'European Portuguese';
        } else if (normalizedTargetLang.includes('brazilian')) {
          return 'Brazilian Portuguese';
        }
        // Default to European Portuguese if just "Portuguese"
        return 'European Portuguese';
      } else if (normalizedTargetLang.includes('spanish')) {
        if (normalizedTargetLang.includes('european')) {
          return 'European Spanish';
        } else if (normalizedTargetLang.includes('latin')) {
          return 'Latin American Spanish';
        }
        // Default to Latin American Spanish if just "Spanish"
        return 'Latin American Spanish';
      }
    }
    
    // Return the actual unsupported language instead of defaulting to English
    return targetLang;
  };

  // Get voice name for the current language
  const getVoiceNameForLanguage = (voiceId: string | null): string => {
    if (!voiceId) return 'Unknown Voice';
    
    const language = getCurrentLanguage();
    return getVoiceDisplayName(voiceId, language);
  };

  const getLanguageSampleUrl = (voice: string): string => {
    const language = getCurrentLanguage();
    
    const fileLanguageName = {
      'English': 'english',
	  'British English': 'BritishEnglish',
      'German': 'German', 
      'French': 'French',
      'Italian': 'Italian',
      'Dutch': 'Dutch',
      'European Portuguese': 'EUPortuguese',
      'Brazilian Portuguese': 'BRPortuguese',
      'European Spanish': 'EUSpanish',
      'Latin American Spanish': 'LASpanish'
    }[language] || 'English';
    
    // Try language-specific sample first, fallback to English
    const baseUrl = 'https://storage.googleapis.com/audiobook-samples';
    const languageSpecificUrl = `${baseUrl}/${voice}_${fileLanguageName}.mp3`;
    
    return languageSpecificUrl;
  }

  const stopPlayingAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
      setPlayingVoiceId(null);
    }
  };

  useEffect(() => {
    if (!isVisible && currentAudio) {
      stopPlayingAudio();
    }
  }, [isVisible]);

  // 1. Do we actually have anything selected?
  const hasChapterSelection  = includedChapters.size > 0;
  const hasIntroSelection    = introEnabled    && introAudioStatus.status !== 'all_audio';
  const hasOutroSelection    = outroEnabled    && outroAudioStatus.status !== 'all_audio';

  // 2. If none of those, there's literally *nothing* to include:
  const nothingSelectable    = !(hasChapterSelection || hasIntroSelection || hasOutroSelection);

  // Total word count and dynamic cost
  const totalWordCount =
    (hasIntroSelection ? countWords(introText) : 0) +
    (hasOutroSelection ? countWords(outroText) : 0) +
    Array.from(includedChapters).reduce((sum, id) => {
      return sum + getChapterWordCount(id);
    }, 0);
	
  const costPerWord = selectedAiModel === 'ElevenLabs' ? 5 : 2;
  const dynamicCreditCost = totalWordCount * costPerWord;
  const [approxUsd, setApproxUsd] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (!shouldShowDollarEquivalents()) { setApproxUsd(null); return; }
        const credits = dynamicCreditCost || 0;
        if (!credits || credits <= 0) { setApproxUsd(null); return; }
        const planID = planId || ((typeof creditBalance === 'object' && creditBalance && (creditBalance as any).planID)
          ? (creditBalance as any).planID
          : undefined);
        const usd = await estimateUSD(credits, planID);
        if (!cancelled) setApproxUsd(formatUSD(usd));
      } catch { if (!cancelled) setApproxUsd(null); }
    };
    run();
    return () => { cancelled = true; };
  }, [dynamicCreditCost, creditBalance, planId]);

  // 3. Now disable "Create Audiobook" if:
  //    – not enough credits, OR
  //    – no voice chosen,    OR
  //    – nothing selected,   OR
  //    – there are any errors
  const isCreateDisabled =
    creditBalance < dynamicCreditCost ||
    !selectedVoice ||
    nothingSelectable ||
    errorMessages.length > 0;

  // 4. Update your disable‐reasons list to match
  const createDisabledReasons: string[] = [];
  if (creditBalance < dynamicCreditCost) {
    createDisabledReasons.push(
      `Not enough credits: need ${dynamicCreditCost}, available ${creditBalance}`
    );
  }
  if (!selectedVoice) {
    createDisabledReasons.push('Please select a voice.');
  }
  if (nothingSelectable) {
    createDisabledReasons.push('Nothing selected to include.');
  }
  if (errorMessages.length > 0) {
    createDisabledReasons.push('Resolve all errors before creating the audiobook.');
  }
  const availableVoices = getAvailableVoices();
  const selectedVoiceOption = voiceOptions.find((voice) => voice.value === selectedVoice);
  const selectedVoiceName = selectedVoice ? getVoiceNameForLanguage(selectedVoice) : '';
  const footerCostSummary = (
    <div className="translation-modal-footer-summary" aria-live="polite" data-testid="tutorial:audiobook:create-cost-summary">
      <span>
        <strong>{dynamicCreditCost.toLocaleString()} credits</strong> required
      </span>
      <span className="translation-modal-footer-summary__separator">·</span>
      <span>
        <strong>{typeof creditBalance === 'number' ? creditBalance.toLocaleString() : 'Loading...'}</strong>{' '}
        available
      </span>
      {creditBalance < dynamicCreditCost ? (
        <>
          <span className="translation-modal-footer-summary__separator">·</span>
          <span className="translation-modal-footer-summary__warning">Top-up needed</span>
        </>
      ) : null}
      <span className="translation-modal-footer-summary__separator">·</span>
      <span>{totalWordCount.toLocaleString()} words</span>
      {isCreateDisabled && createDisabledReasons.length > 0 ? (
        <>
          <span className="translation-modal-footer-summary__separator">·</span>
          <span className="audiobook-creation-footer-blocker">Action needed: {createDisabledReasons[0]}</span>
        </>
      ) : null}
      <Button
        type="link"
        size="small"
        className="translation-modal-footer-summary__details"
        aria-label="View audiobook cost details"
        onClick={() => setIsCostDetailsVisible(true)}
      >
        View details
      </Button>
    </div>
  );

const handleCreateAudiobook = async () => {
  if (!isVisible) return;
  onCancel();

  try {
    const options: CreateOptions = {
      version: selectedVersion,
      voice: selectedVoice,
      selectedChapters: Array.from(includedChapters),
      introText: introAudioStatus.status === 'all_audio' || !introEnabled ? '' : introText,
      outroText: outroAudioStatus.status === 'all_audio' || !outroEnabled ? '' : outroText,
      includeChapterTitles,
      aiModel: selectedAiModel,
    };

    await onCreateAudiobook(options);
  } catch (error) {
    message.error('Error creating audiobook. Please try again.');
  }
};

  return (
    <>
    <Modal
      title={<div className="audiobook-creation-title">Generate {getCurrentLanguage()} Audiobook</div>}
      open={isVisible}
      onCancel={onCancel}
      afterOpenChange={onAfterOpenChange}
      className="audiobook-creation-modal translation-modal-footer-stable"
      footer={[
        <React.Fragment key="cost-summary">
          {footerCostSummary}
        </React.Fragment>,
        <Button key="cancel" onClick={onCancel} data-testid="tutorial:audiobook:create-cancel">Cancel</Button>,
        <Button
          key="create"
          type="primary"
          data-tour-id="audiobook-modal-submit"
          data-testid="tutorial:audiobook:create-start"
          onClick={handleCreateAudiobook}
          disabled={isCreateDisabled}
        >
          Create Audiobook
        </Button>,
      ]}
      width={825}
      bodyStyle={{ 
        maxHeight: 'calc(85vh - 100px)', 
        overflowY: 'auto',
        overflowX: 'hidden' 
      }}
    >
  {/* Loading overlay - appears on top of all content when loading */}
  {isLoadingStatus && (
    <div className="audiobook-creation-loading-overlay">
      <LoadingCard label="Loading chapter audio status..." />
    </div>
  )}

{isUnsupportedLanguage && (
  <Alert
    message={`${unsupportedLanguageName} is not supported for audio generation at this time.`}
    type="error"
    icon={<ExclamationCircleOutlined />}
    className="audiobook-creation-alert"
    showIcon
  />
)}
  
  {/* Wrapper div around all modal content */}
<div className="audiobook-creation-body" data-testid="tutorial:audiobook:create-modal" style={{ opacity: isLoadingStatus ? 0.6 : 1 }}>
{isCreateDisabled && createDisabledReasons.length > 0 && (
  <section className="audiobook-creation-block audiobook-creation-block--attention" aria-live="polite" data-testid="tutorial:audiobook:create-readiness">
    <div className="audiobook-creation-block__header">
      <div>
        <span>Requirements</span>
        <strong>Resolve before creating</strong>
      </div>
    </div>
    <div className="audiobook-creation-readiness-list">
      {createDisabledReasons.map((reason) => (
        <div key={reason} className="translation-readiness-support-note translation-readiness-support-note--blocking">
          {reason}
        </div>
      ))}
    </div>
  </section>
)}
<section className="audiobook-creation-block" data-tour-id="audiobook-modal-voices" data-testid="tutorial:audiobook:create-voice-model">
  <div className="audiobook-creation-block__header">
    <div>
      <span>Voice and model</span>
      <strong>Narration setup</strong>
    </div>
    <Tag>{selectedAiModel}</Tag>
  </div>
<Row gutter={[12, 12]} className="audiobook-creation-control-grid">
  <Col xs={24} md={12}>
    <div className="audiobook-creation-field-row">
      <label><strong>AI Model:</strong></label>
      <Select
        value={selectedAiModel}
        onChange={setSelectedAiModel}
        style={{ flex: 1 }}
        disabled={isUnsupportedLanguage}
        suffixIcon={
          <span className="ant-select-suffix">
            <DownOutlined className="audiobook-creation-select-icon" />
          </span>
        }
      >
        {aiModelOptions.map(model => (
          <Select.Option key={model.value} value={model.value}>
            <Space size="small">
              <span>{model.label}</span>
              {model.value === 'OpenAI' && <Tag className="audiobook-creation-status-tag is-success">Most Affordable</Tag>}
              {model.value === 'ElevenLabs' && <Tag className="audiobook-creation-status-tag is-info">Best Quality</Tag>}
            </Space>
          </Select.Option>
        ))}
      </Select>
    </div>
  </Col>
  <Col xs={24} md={12}>
    <div className="audiobook-creation-voice-control">
      <div className="audiobook-creation-field-row">
        <label><strong>Voice:</strong></label>
        <div className="audiobook-creation-voice-picker">
          <Select
            value={selectedVoice || undefined}
            onChange={setSelectedVoice}
            className="audiobook-creation-voice-select"
            placeholder="Select a voice"
            disabled={isUnsupportedLanguage}
            aria-label="Voice selection"
            optionLabelProp="label"
            popupMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 320 }}
            classNames={{ popup: { root: 'audiobook-creation-voice-popup' } }}
            suffixIcon={<span className="ant-select-suffix"><DownOutlined className="audiobook-creation-select-icon" /></span>}
          >
            <Select.OptGroup label="Voices">
              {availableVoices.map(v => {
                const voiceName = getVoiceNameForLanguage(v.value);
                const isPlaying = playingVoiceId === v.value;

                return (
                  <Select.Option key={v.value} value={v.value} label={voiceName}>
                    <div className="audiobook-creation-voice-option">
                      <span className="audiobook-creation-voice-option__label">
                        <span
                          className={`audiobook-creation-voice-option__dot ${
                            v.gender === 'Female'
                              ? 'audiobook-creation-voice-option__dot--female'
                              : 'audiobook-creation-voice-option__dot--male'
                          }`}
                        />
                        <span className="audiobook-creation-voice-option__name">{voiceName}</span>
                        <span className="audiobook-creation-voice-option__gender">{v.gender}</span>
                      </span>
                      <Tooltip title={isPlaying ? 'Stop sample' : 'Play sample'}>
                        <Button
                          type="text"
                          size="small"
                          icon={isPlaying ? <PauseOutlined /> : <SoundOutlined />}
                          aria-label={`${isPlaying ? 'Stop' : 'Play'} ${voiceName} sample`}
                          onMouseDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          onClick={(e) => playSample(v.sampleUrl, v.provider, v.value, e)}
                        />
                      </Tooltip>
                    </div>
                  </Select.Option>
                );
              })}
            </Select.OptGroup>
          </Select>
          <Tooltip title={selectedVoiceOption ? `Play ${selectedVoiceName} sample` : 'Select a voice to play a sample'}>
            <Button
              className="audiobook-creation-voice-preview"
              icon={playingVoiceId === selectedVoice ? <PauseOutlined /> : <SoundOutlined />}
              aria-label={selectedVoiceOption ? `Play ${selectedVoiceName} sample` : 'Play selected voice sample'}
              disabled={!selectedVoiceOption || isUnsupportedLanguage}
              onClick={(e) => {
                if (!selectedVoiceOption) return;
                playSample(selectedVoiceOption.sampleUrl, selectedVoiceOption.provider, selectedVoiceOption.value, e);
              }}
            >
              Sample
            </Button>
          </Tooltip>
        </div>
      </div>
      <div className="audiobook-creation-voice-filter">
        <Radio.Group
          value={genderFilter}
          aria-label="Voice gender filter"
          onChange={(e) => setGenderFilter(e.target.value as 'all' | 'Female' | 'Male')}
        >
          <Radio.Button value="all">All</Radio.Button>
          <Radio.Button value="Female">Female</Radio.Button>
          <Radio.Button value="Male">Male</Radio.Button>
        </Radio.Group>
      </div>
    </div>
  </Col>
</Row>
</section>

      {/* Chapters section - with improved scrolling UI */}
  <section
    data-tour-id="audiobook-modal-chapters"
    data-testid="tutorial:audiobook:create-chapters"
    className="audiobook-creation-block"
    style={{
    pointerEvents: isLoadingStatus || isUnsupportedLanguage ? 'none' : 'auto',
    opacity: isUnsupportedLanguage ? 0.3 : 1
  }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}> 		
          <Space>
            <label><strong>Chapters to include:</strong></label>
            {isLoadingStatus && <span>Loading audio status...</span>}
            {statusError && <Text type="danger">Error: {statusError}</Text>}
          </Space>
          <Text type={"secondary"}>
            {includedChapters.size} of {chapters.filter(c => getChapterWordCount(c.id) > 0).length} chapters selected
            {totalSelectedWordCount > 0 && ` (${totalSelectedWordCount.toLocaleString()} words)`}
          </Text>
        </div>
{/* Add chapter‐title toggle 
<Space style={{ marginBottom: 12 }}>
  <Checkbox
    checked={includeChapterTitles}
    onChange={e => setIncludeChapterTitles(e.target.checked)}
  >
    Add chapter title to each chapter
  </Checkbox>
  <Tooltip title="Check this option only if the chapter title/number is not already in the chapter text and the chapter titles are correctly populated">
    <QuestionCircleOutlined />
  </Tooltip>
</Space> */}
        <Space style={{ marginBottom: 8 }} wrap>
          <Button 
            size="small" 
            onClick={selectAll}
            icon={<CheckCircleOutlined />}
          >
            Select All
          </Button>
          <Button 
            size="small" 
            onClick={deselectAll}
            icon={<StopOutlined />}
          >
            Deselect All
          </Button>
          <Button 
            size="small"
            onClick={invertSelection}
            icon={<SwapOutlined />}
          >
            Invert Selection
          </Button>
          <Divider type="vertical" />
          <Input.Search
            placeholder="Filter chapters..."
            size="small"
            style={{ width: 200 }}
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            onSearch={value => setFilterText(value)}
            allowClear
            prefix={<SearchOutlined />}
          />
        </Space>       
        {/* Improved chapter scrolling area with visual indicators */}
        <div className="audiobook-creation-chapter-scroll">
          <div className="audiobook-creation-chapter-grid">
            {filteredChapters.map((chap, index) => {
              const isIncluded = includedChapters.has(chap.id);
              const wordCount = getChapterWordCount(chap.id);
              const hasWords = wordCount > 0;
              const audioStatus = chapterAudioStatus[chap.id] || 'no_audio';
              const isCompleted = audioStatus === 'all_audio';
              const hasError = audioStatus === 'error';
              const isSelectable = hasWords && !isCompleted && !hasError;
              const chapterStateClass = !hasWords
                ? 'is-empty'
                : isCompleted
                  ? 'is-complete'
                  : hasError
                    ? 'is-error'
                    : audioStatus === 'partial_audio'
                      ? 'is-partial'
                      : isIncluded
                        ? 'is-included'
                        : 'is-excluded';
              const disabledReason = !hasWords
                ? 'No words available for this chapter.'
                : isCompleted
                  ? 'Complete audio already exists for this chapter.'
                  : hasError
                    ? 'Resolve the chapter audio error before selecting it.'
                    : '';

              return (
                <div 
                  key={chap.id}
                  data-testid={`chapter-row-${chap.id}`}
                  className={`audiobook-creation-chapter-row ${chapterStateClass}${!isSelectable ? ' is-disabled' : ''}`}
                  title={disabledReason || undefined}
                  aria-disabled={!isSelectable}
                  onClick={isSelectable ? (e) => handleChapterClick(chap.id, index, e) : undefined}
                >
                  <div className="audiobook-creation-chapter-main">
                    <Checkbox
                      checked={isIncluded}
                      disabled={!isSelectable}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => isSelectable && toggleChapterInclusion(chap.id, index)}
                    />
                    <span 
                      className="audiobook-creation-chapter-title"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isSelectable) toggleChapterInclusion(chap.id, index);
                      }}
                      title={chap.title}
                    >
                      {chap.title}
                    </span>
                  </div>
                  <div className="audiobook-creation-chapter-meta">
  {isCompleted && (
    <Tooltip title={
      chapterVoices[chap.id] 
        ? `Voice: ${getVoiceNameForLanguage(chapterVoices[chap.id])}` 
        : 'No voice information'
    }>
      <Tag className="audiobook-creation-status-tag is-info">Complete</Tag>
    </Tooltip>
  )}
  {hasError && <Tag className="audiobook-creation-status-tag is-danger">Error</Tag>}
  {audioStatus === 'partial_audio' && (
    <Tooltip title={
      chapterVoices[chap.id] 
        ? `Voice: ${getVoiceNameForLanguage(chapterVoices[chap.id])}` 
        : 'No voice information'
    }>
      <Tag className="audiobook-creation-status-tag is-warning">Partial</Tag>
    </Tooltip>
  )}
                    <span className="audiobook-creation-chapter-words">
                      {wordCount.toLocaleString()} words
                    </span>
                    {!isSelectable && disabledReason ? (
                      <span className="audiobook-creation-chapter-reason">{disabledReason}</span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
          {filteredChapters.length === 0 && (
            <div style={{ padding: '15px', textAlign: 'center', color: 'var(--text-secondary)' }}>
              No chapters match your filter
            </div>
          )}
        </div>
        
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          Click a chapter to toggle selection. Chapters with complete audio or errors cannot be selected.
        </div>
      </section>

      {/* Intro/Outro in two columns */}
      <section className="audiobook-creation-block" data-tour-id="audiobook-modal-credits" data-testid="tutorial:audiobook:create-intro-outro">
        <div className="audiobook-creation-block__header">
          <div>
            <span>Opening and closing</span>
            <strong>Intro and outro credits</strong>
          </div>
          <Tag>{introEnabled || outroEnabled ? 'Included when selected' : 'Disabled'}</Tag>
        </div>
      <Row
        gutter={16}
        style={{ opacity: isUnsupportedLanguage ? 0.3 : 1 }}
      >
        <Col span={12}>
<div>
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
<label>
      <Checkbox 
        checked={introEnabled} 
        onChange={(e) => setIntroEnabled(e.target.checked)}
        disabled={introAudioStatus.status === 'all_audio'}
        style={{ marginRight: 5 }}
      />
  <strong>Intro Credits:</strong> 
  <Tooltip title="If you are creating an audiobook of a translation, this intro will be translated into that language.">
    <QuestionCircleOutlined style={{ marginLeft: 5 }} />
  </Tooltip> 		
  <Text type="secondary" style={{ marginLeft: 5 }}>({introWordCount} words)</Text>
  {introAudioStatus.status === 'all_audio' && (
    <Tooltip title={
      introAudioStatus.voice 
        ? `Voice: ${getVoiceNameForLanguage(introAudioStatus.voice)}` 
        : 'No voice information'
    }>
      <Tag className="audiobook-creation-status-tag is-info">Complete</Tag>
    </Tooltip>
  )}
  {introAudioStatus.status === 'partial_audio' && (
    <Tooltip title={
      introAudioStatus.voice 
        ? `Voice: ${getVoiceNameForLanguage(introAudioStatus.voice)}` 
        : 'No voice information'
    }>
      <Tag className="audiobook-creation-status-tag is-warning">Partial</Tag>
    </Tooltip>
  )}
</label>
<Button
  type="text"
  icon={<RedoOutlined />}
  size="small"
  title="Reset to default intro text"
  onClick={() => {
    setIntroText(generateDefaultIntroText());
    setIntroEdited(false);
  }}
  disabled={!introEdited || introAudioStatus.status === 'all_audio' || isUnsupportedLanguage || !introEnabled}
/>
  </div>
  <Input.TextArea
    value={introAudioStatus.status === 'all_audio' && introAudioStatus.processed_text
      ? introAudioStatus.processed_text
      : introText}
    onChange={e => {
      setIntroText(e.target.value);
      setIntroEdited(true);
    }}
    rows={4}
    placeholder="Enter intro text..."
    disabled={introAudioStatus.status === 'all_audio' || !introEnabled}
  />
  <Space style={{ marginTop: 8 }}>
    <Button
      icon={<TranslationOutlined />}
      onClick={() => translateText(introText, 'intro')}
      loading={isTranslatingIntro}
      disabled={
        !isTranslationSelected() ||
        !introEnabled ||
        !introText.trim() ||
        introAudioStatus.status === 'all_audio' ||
        !onTranslateText
      }
    >
      Translate Intro
    </Button>
  </Space>
</div>
        </Col>
        <Col span={12}>
<div>
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
<label>
      <Checkbox 
        checked={outroEnabled} 
        onChange={(e) => setOutroEnabled(e.target.checked)}
        disabled={outroAudioStatus.status === 'all_audio'}
        style={{ marginRight: 5 }}
      />
  <strong>Outro Credits:</strong>
  <Tooltip title="If you are creating an audiobook of a translation, this outro will be translated into that language.">
    <QuestionCircleOutlined style={{ marginLeft: 5 }} />
  </Tooltip>		
  <Text type="secondary" style={{ marginLeft: 5 }}>({outroWordCount} words)</Text>
  {outroAudioStatus.status === 'all_audio' && (
    <Tooltip title={
      outroAudioStatus.voice 
        ? `Voice: ${getVoiceNameForLanguage(outroAudioStatus.voice)}` 
        : 'No voice information'
    }>
      <Tag className="audiobook-creation-status-tag is-info">Complete</Tag>
    </Tooltip>
  )}
  {outroAudioStatus.status === 'partial_audio' && (
    <Tooltip title={
      outroAudioStatus.voice 
        ? `Voice: ${getVoiceNameForLanguage(outroAudioStatus.voice)}` 
        : 'No voice information'
    }>
      <Tag className="audiobook-creation-status-tag is-warning">Partial</Tag>
    </Tooltip>
  )}
</label>
<Button
  type="text"
  icon={<RedoOutlined />}
  size="small"
  title="Reset to default outro text"
  onClick={() => {
    setOutroText(generateDefaultOutroText());
    setOutroEdited(false);
  }}
  disabled={!outroEdited || outroAudioStatus.status === 'all_audio' || isUnsupportedLanguage || !outroEnabled}
/>
  </div>
  <Input.TextArea
    value={outroAudioStatus.status === 'all_audio' && outroAudioStatus.processed_text
      ? outroAudioStatus.processed_text
      : outroText}
    onChange={e => {
      setOutroText(e.target.value);
      setOutroEdited(true);
    }}
    rows={4}
    placeholder="Enter outro text..."
    disabled={outroAudioStatus.status === 'all_audio' || !outroEnabled}
  />
  <Space style={{ marginTop: 8 }}>
    <Button
      icon={<TranslationOutlined />}
      onClick={() => translateText(outroText, 'outro')}
      loading={isTranslatingOutro}
      disabled={
        !isTranslationSelected() ||
        !outroEnabled ||
        !outroText.trim() ||
        outroAudioStatus.status === 'all_audio' ||
        !onTranslateText
      }
    >
      Translate Outro
    </Button>
  </Space>
</div>
        </Col>
      </Row>
      </section>

	  {/* Inline-styled warnings */}
      {warningMessages.length > 0 && (
        <div className="audiobook-creation-message-list">
          {warningMessages.map((msg, i) => (
            <div key={i} className="audiobook-creation-message-row is-warning">
              <WarningOutlined />
              <span>{msg}</span>
            </div>
          ))}
        </div>
      )}

      {/* Inline-styled errors */}
      {errorMessages.length > 0 && (
        <div className="audiobook-creation-message-list">
          {errorMessages.map((msg, i) => (
            <div key={i} className="audiobook-creation-message-row is-error">
              <ExclamationCircleOutlined />
              <span>{msg}</span>
            </div>
          ))}
        </div>
      )}

	  </div>
    </Modal>
    <Modal
      title={<div className="audiobook-creation-title">Audiobook Cost Details</div>}
      open={isCostDetailsVisible}
      onCancel={() => setIsCostDetailsVisible(false)}
      className="audiobook-creation-modal"
      width={560}
      footer={[
        <Button key="close" type="primary" onClick={() => setIsCostDetailsVisible(false)}>
          Close
        </Button>,
      ]}
    >
      <div className="audiobook-creation-cost-details">
        <section className="translation-readiness-panel translation-readiness-panel--cost">
          <div className="translation-modal-subheading">Credit estimate</div>
          <div className="audiobook-creation-cost-grid">
            <div className="audiobook-creation-cost-item">
              <span>Required</span>
              <strong>{dynamicCreditCost.toLocaleString()} credits</strong>
              {approxUsd && <small>approx. {approxUsd}</small>}
            </div>
            <div className="audiobook-creation-cost-item">
              <span>Available</span>
              <strong className={creditBalance < dynamicCreditCost ? 'is-warning' : undefined}>
                {typeof creditBalance === 'number' ? creditBalance.toLocaleString() : 'Loading...'} credits
              </strong>
            </div>
            <div className="audiobook-creation-cost-item">
              <span>Rate</span>
              <strong>{costPerWord} credits / word</strong>
            </div>
          </div>
        </section>
        <section className="translation-readiness-panel">
          <div className="translation-modal-subheading">Selected output</div>
          <div className="audiobook-creation-cost-grid">
            <div className="audiobook-creation-cost-item">
              <span>Chapters</span>
              <strong>{includedChapters.size} selected</strong>
              <small>{totalSelectedWordCount.toLocaleString()} words</small>
            </div>
            <div className="audiobook-creation-cost-item">
              <span>Intro</span>
              <strong>{hasIntroSelection ? 'Included' : 'Skipped'}</strong>
              <small>{introWordCount.toLocaleString()} words</small>
            </div>
            <div className="audiobook-creation-cost-item">
              <span>Outro</span>
              <strong>{hasOutroSelection ? 'Included' : 'Skipped'}</strong>
              <small>{outroWordCount.toLocaleString()} words</small>
            </div>
          </div>
        </section>
        <section className="translation-readiness-panel">
          <div className="translation-modal-subheading">Requirements</div>
          {isCreateDisabled && createDisabledReasons.length > 0 ? (
            createDisabledReasons.map((reason) => (
              <div key={reason} className="translation-readiness-support-note translation-readiness-support-note--blocking">
                {reason}
              </div>
            ))
          ) : (
            <div className="translation-readiness-support-note translation-readiness-support-note--success">
              Ready to create {includedChapters.size} chapters, {totalWordCount.toLocaleString()} words, {dynamicCreditCost.toLocaleString()} credits.
            </div>
          )}
        </section>
      </div>
    </Modal>
    </>
  );
};

export default AudiobookCreationModal;
